import React from "react";
import sideStyles from './SideTabShared.module.css';
/** Import types. */
import type {Evaluation, WaterfallItem} from "../../base/ui_general/GeneralInterfaces.tsx";
import {EvaluationFieldEnum} from "../Interfaces.tsx";
import {WaterfallItemTypeEnum, EvaluationStatusEnum} from "../../base/Settings.tsx";
import type {DesignCenterTabProps} from "../Interfaces.tsx";
import {formatPercent} from "../../base/utils/GeneralUtilities";
import {Alert} from "react-bootstrap";

/** Props for the ResultsTab component. */
interface ResultTabProps extends DesignCenterTabProps {
    evaluation: Evaluation | undefined;
    waterfallItems: Array<WaterfallItem>;
    /** Callback to show histogram in the main screen */
    showLossDistribution: (data: number[], title: string) => void;
}


/** Component to display evaluation results for each waterfall item. */
export function ResultsTab(props: ResultTabProps) {
    if (!props.evaluation || props.evaluation.status === EvaluationStatusEnum.PENDING) {
        return (
            <div className={sideStyles.sideCenterFill}>
                <span className={sideStyles.sideLoadingGlyph}>⏳</span>
            </div>
        );
    }
    if (props.evaluation.status === EvaluationStatusEnum.ERROR) {
        const msg =
            props.evaluation.error_description
                ? `Evaluation failed: ${props.evaluation.error_description}`
                : "Evaluation failed."
        return (
            <Alert variant="danger" className="d-flex align-items-center">
                <span>{msg}</span>
            </Alert>
        );
    }
    if (props.evaluation.status === EvaluationStatusEnum.RUNNING) {
        return (
            <div className={sideStyles.sideCenterFill}>
                <div className={sideStyles.sideSpinner}/>
            </div>
        );
    }
    if (!props.evaluation.evaluation_results || props.evaluation.evaluation_results.length === 0) {
        return null;
    }
    const filteredResults = props.evaluation.evaluation_results.filter((er) => {
        const wfItem = props.waterfallItems.find((w) => w.nr === er.waterfall_item_nr);
        return wfItem?.type === WaterfallItemTypeEnum.CASH_TRANCHE ||
            wfItem?.type === WaterfallItemTypeEnum.SYNTHETIC_TRANCHE;
    });
    if (filteredResults.length === 0) {
        return <div>No evaluation results for CASH or SYNTHETIC tranches.</div>;
    }

    const formatValue = (field: EvaluationFieldEnum, value: number | undefined) => {
        if (value === undefined || value === null) return "-";
        switch (field) {
            case EvaluationFieldEnum.WAL:
                return value.toFixed(2) + ' yr';
            case EvaluationFieldEnum.FairValue:
            case EvaluationFieldEnum.FairPremium:
            case EvaluationFieldEnum.PD:
            case EvaluationFieldEnum.PD_lifetime:
            case EvaluationFieldEnum.LGD:
            case EvaluationFieldEnum.EL:
            case EvaluationFieldEnum.VaR:
            case EvaluationFieldEnum.RiskAdjustedFairValue:
            case EvaluationFieldEnum.RiskAdjustedFairPremium:
                return formatPercent(value);
            default:
                return value.toString();
        }
    };

    /** Show units of variables. */
    const fieldLabels: Record<EvaluationFieldEnum, string> = {
        [EvaluationFieldEnum.WAL]: "WAL",
        [EvaluationFieldEnum.WALDistribution]: "WAL distribution",
        [EvaluationFieldEnum.FairValue]: "Fair Value",
        [EvaluationFieldEnum.FairValueDistribution]: "Fair Value distribution",
        [EvaluationFieldEnum.FairPremium]: "Fair Premium",
        [EvaluationFieldEnum.PD]: "PD",
        [EvaluationFieldEnum.PD_lifetime]: "PD (lifetime)",
        [EvaluationFieldEnum.LGD]: "LGD",
        [EvaluationFieldEnum.EL]: "EL",
        [EvaluationFieldEnum.VaR]: "VaR",
        [EvaluationFieldEnum.RiskAdjustedFairValue]: "Risk-adj. Fair Value",
        [EvaluationFieldEnum.RiskAdjustedFairPremium]: "Risk-adj. Fair Prem.",
        [EvaluationFieldEnum.LossDistribution]: "Loss Distribution",
    };

    return (
        <div className={sideStyles.sideTabContainer}>
            <div className={sideStyles.sideHeaderSticky}>Evaluation Results</div>
            <div className={`${sideStyles.sideContentArea} ${sideStyles.sideGrid}`}>
                {filteredResults.map((er) => {
                    const wfItem = props.waterfallItems.find((w) => w.nr === er.waterfall_item_nr);
                    return (
                        <div
                            key={er.evaluation_id + er.waterfall_item_nr}
                            className={`${sideStyles.sideCard} ${sideStyles.sideCardBodyPadding} ${sideStyles.sideResultCardBody}`}
                        >
                            {/* Header */}
                            <div className={sideStyles.sideCardHeaderRow}>
                                <span
                                    className={`${sideStyles.sideSectionTitle}`}>{wfItem?.name ?? "Unknown name"}</span>
                                <span className={`${sideStyles.sideSubtitleMuted} ${sideStyles.sideTitleEllipsis}`}>
                                    ({wfItem?.type ?? "Unknown type"})
                                </span>
                            </div>

                            <div className={sideStyles.sideFieldsGrid}>
                                {([
                                    [EvaluationFieldEnum.WAL, er.wal],
                                    [EvaluationFieldEnum.FairValue, er.fair_value],
                                    [EvaluationFieldEnum.FairPremium, er.fair_premium_spread],
                                    [EvaluationFieldEnum.PD, er.pd],
                                    [EvaluationFieldEnum.PD_lifetime, er.pd_lifetime],
                                    [EvaluationFieldEnum.LGD, er.lgd],
                                    [EvaluationFieldEnum.EL, er.el],
                                    [EvaluationFieldEnum.VaR, er.var],
                                    [EvaluationFieldEnum.RiskAdjustedFairValue, er.risk_adjusted_fair_value],
                                    [EvaluationFieldEnum.RiskAdjustedFairPremium, er.risk_adjusted_fair_premium_spread],
                                ] as const).map(([field, value]) => {
                                    const isLinkField =
                                        field === EvaluationFieldEnum.EL ||
                                        field === EvaluationFieldEnum.WAL ||
                                        field === EvaluationFieldEnum.FairValue;

                                    // Pick the right distribution and title based on the field
                                    const showDist = () => {
                                        if (field === EvaluationFieldEnum.WAL) {
                                            props.showLossDistribution(
                                                er.wal_distribution,
                                                `${wfItem?.name ?? "Unknown"} WAL Distribution`
                                            );
                                        } else if (field === EvaluationFieldEnum.EL) {
                                            props.showLossDistribution(
                                                er.loss_distribution,
                                                `${wfItem?.name ?? "Unknown"} Loss Distribution`
                                            );
                                        } else {
                                            props.showLossDistribution(
                                                er.fair_value_distribution,
                                                `${wfItem?.name ?? "Unknown"} Fair Value Distribution`
                                            );
                                        }
                                    };

                                    return (
                                        <React.Fragment key={field}>
                                            <span className={sideStyles.sideFieldLabel}>
                                               {fieldLabels[field]}
                                             </span>
                                            <span
                                                className={isLinkField ? sideStyles.sideFieldValueClickable : sideStyles.sideFieldValue}
                                                onClick={isLinkField ? showDist : undefined}
                                            >
                                                 {isLinkField ? formatValue(field, value) : formatValue(field, value)}
                                             </span>
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
